import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { brandingApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import {
  Paintbrush,
  Save,
  Loader2,
  Globe,
  Image,
  Type,
  Palette,
  Lock,
  CheckCircle2,
} from 'lucide-react';
import clsx from 'clsx';

// ============================================================================
// Types
// ============================================================================

interface BrandingConfig {
  logo: string | null;
  favicon: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  appName: string | null;
  loginBackground: string | null;
  companyName: string | null;
  domain: string | null;
}

const DEFAULT_CONFIG: BrandingConfig = {
  logo: null,
  favicon: null,
  primaryColor: '#7c3aed',
  secondaryColor: null,
  appName: null,
  loginBackground: null,
  companyName: null,
  domain: null,
};

const COLOR_PRESETS = [
  { name: 'Violet', primary: '#7c3aed', secondary: '#a78bfa' },
  { name: 'Bleu', primary: '#3b82f6', secondary: '#93c5fd' },
  { name: 'Émeraude', primary: '#10b981', secondary: '#6ee7b7' },
  { name: 'Rose', primary: '#ec4899', secondary: '#f9a8d4' },
  { name: 'Cyan', primary: '#06b6d4', secondary: '#67e8f9' },
  { name: 'Orange', primary: '#f97316', secondary: '#fdba74' },
];

// ============================================================================
// Page
// ============================================================================

export default function BrandingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['branding-status', tenantKey],
    queryFn: () => brandingApi.getStatus(),
  });

  const { data: brandingData, isLoading: brandingLoading } = useQuery({
    queryKey: ['branding', tenantKey],
    queryFn: () => brandingApi.get(),
    enabled: statusData?.data?.enabled === true,
  });

  const isEnabled = statusData?.data?.enabled === true;
  const [config, setConfig] = useState<BrandingConfig>(DEFAULT_CONFIG);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (brandingData?.data) {
      setConfig({ ...DEFAULT_CONFIG, ...brandingData.data });
    }
  }, [brandingData]);

  const saveMutation = useMutation({
    mutationFn: () => brandingApi.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const handleFileUpload = (field: 'logo' | 'favicon' | 'loginBackground') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        alert('Fichier trop volumineux (max 2 Mo)');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setConfig((prev) => ({ ...prev, [field]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  if (statusLoading || brandingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  // Plan doesn't include white-label
  if (!isEnabled) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Paintbrush className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('branding.title')}</h1>
            <p className="text-sm text-gray-500">{t('branding.subtitle')}</p>
          </div>
        </div>

        <div className="glass-card p-8 text-center">
          <Lock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('branding.feature_off_title')}</h2>
          <p className="text-gray-500 max-w-md mx-auto">
            {t('branding.feature_off_body')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Paintbrush className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('branding.title')}</h1>
            <p className="text-sm text-gray-500">{t('branding.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saved ? t('branding.saved') : t('branding.save')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identity */}
        <div className="glass-card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Type className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('branding.section_identity')}</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('branding.field_app_name')}</label>
            <input
              type="text"
              value={config.appName || ''}
              onChange={(e) => setConfig((p) => ({ ...p, appName: e.target.value || null }))}
              placeholder="TrackFleet"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('branding.field_company_name')}</label>
            <input
              type="text"
              value={config.companyName || ''}
              onChange={(e) => setConfig((p) => ({ ...p, companyName: e.target.value || null }))}
              placeholder={t('branding.company_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Globe className="h-4 w-4" /> {t('branding.field_domain')}
            </label>
            <input
              type="text"
              value={config.domain || ''}
              onChange={(e) => setConfig((p) => ({ ...p, domain: e.target.value || null }))}
              placeholder={t('branding.domain_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
            <p className="text-xs text-gray-400 mt-1">
              {t('branding.domain_hint')}
            </p>
          </div>
        </div>

        {/* Colors */}
        <div className="glass-card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Palette className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('branding.section_colors')}</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => setConfig((p) => ({ ...p, primaryColor: preset.primary, secondaryColor: preset.secondary }))}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors',
                  config.primaryColor === preset.primary
                    ? 'border-gray-900 bg-gray-50 font-medium'
                    : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <span className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                {preset.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('branding.color_primary')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={config.primaryColor || '#7c3aed'}
                  onChange={(e) => setConfig((p) => ({ ...p, primaryColor: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={config.primaryColor || ''}
                  onChange={(e) => setConfig((p) => ({ ...p, primaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('branding.color_secondary')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={config.secondaryColor || '#a78bfa'}
                  onChange={(e) => setConfig((p) => ({ ...p, secondaryColor: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={config.secondaryColor || ''}
                  onChange={(e) => setConfig((p) => ({ ...p, secondaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <p className="text-xs text-gray-500 mb-2">{t('branding.preview')}</p>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2 text-white text-sm rounded-lg"
                style={{ backgroundColor: config.primaryColor || '#7c3aed' }}
              >
                {t('branding.btn_primary')}
              </button>
              <button
                className="px-4 py-2 text-white text-sm rounded-lg"
                style={{ backgroundColor: config.secondaryColor || '#a78bfa' }}
              >
                {t('branding.btn_secondary')}
              </button>
              <span
                className="px-3 py-1 text-white text-xs rounded-full"
                style={{ backgroundColor: config.primaryColor || '#7c3aed' }}
              >
                {t('branding.badge')}
              </span>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="glass-card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Image className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('branding.section_logo')}</h2>
          </div>

          <div className="flex items-center gap-6">
            <div
              className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors overflow-hidden"
              onClick={() => handleFileUpload('logo')}
            >
              {config.logo ? (
                <img src={config.logo} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Image className="h-8 w-8 text-gray-300" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">{t('branding.main_logo')}</p>
              <p className="text-xs text-gray-400">{t('branding.logo_format')}</p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleFileUpload('logo')}
                  className="text-xs text-purple-600 hover:text-purple-700"
                >
                  {t('branding.change')}
                </button>
                {config.logo && (
                  <button
                    onClick={() => setConfig((p) => ({ ...p, logo: null }))}
                    className="text-xs text-red-500 hover:text-red-600"
                  >
                    {t('branding.delete')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div
              className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors overflow-hidden"
              onClick={() => handleFileUpload('favicon')}
            >
              {config.favicon ? (
                <img src={config.favicon} alt="Favicon" className="w-full h-full object-contain" />
              ) : (
                <Image className="h-5 w-5 text-gray-300" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">{t('branding.favicon')}</p>
              <p className="text-xs text-gray-400">{t('branding.favicon_format')}</p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleFileUpload('favicon')}
                  className="text-xs text-purple-600 hover:text-purple-700"
                >
                  {t('branding.change')}
                </button>
                {config.favicon && (
                  <button
                    onClick={() => setConfig((p) => ({ ...p, favicon: null }))}
                    className="text-xs text-red-500 hover:text-red-600"
                  >
                    {t('branding.delete')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login background */}
        <div className="glass-card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Image className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('branding.section_login_page')}</h2>
          </div>

          <div
            className="w-full h-40 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors overflow-hidden"
            onClick={() => handleFileUpload('loginBackground')}
          >
            {config.loginBackground ? (
              <img src={config.loginBackground} alt="Background" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <Image className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{t('branding.click_to_upload_bg')}</p>
              </div>
            )}
          </div>
          {config.loginBackground && (
            <button
              onClick={() => setConfig((p) => ({ ...p, loginBackground: null }))}
              className="text-xs text-red-500 hover:text-red-600"
            >
              {t('branding.delete_image')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
